// Copyright 2024, Imprivata, Inc.  All rights reserved.

import { jsonToBase64, stringToBase64 } from '@imprivata-cloud/dataprivacy';
import { QueryParam } from './constants';
import { AuthResource } from './shared/types';
import { WEB_APP_NAME } from './shared/constants';

export const stringToBase64Url = (str: string): string => {
  return base64toBase64Url(stringToBase64(str));
};

export const jsonToBase64Url = (obj: Record<string, unknown>): string => {
  return base64toBase64Url(jsonToBase64(obj));
};

// base64Url is a variant of base64 that is URL and filename safe.
// All it does differently is omitting padding '=' and replacing '+' -> '-' and '/' -> '_'.
export const base64toBase64Url = (str: string): string => {
  return str.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const getUrlParam = (param: string): string | null => {
  return new URLSearchParams(window.location.search).get(param);
};

export const getUrlParamRaw = (param: string): string | undefined => {
  const searchParams = window.location.search.slice(1); // remove leading '?'
  if (!searchParams) return undefined;

  for (const pair of searchParams.split('&')) {
    const [key, value] = pair.split('=');
    if (decodeURIComponent(key) === param) {
      return value !== undefined ? decodeURIComponent(value) : undefined;
    }
  }
  return undefined;
};

// See QueryParam.DisableAutoComplete for why this matches the raw URL string
// instead of parsing query params.
export function hasDisableAutoComplete(url: string): boolean {
  return url.includes(QueryParam.DisableAutoComplete);
}

export const resolveClientName = (clientName?: string): string =>
  clientName ?? WEB_APP_NAME;

export const determineResource = (targetUrl?: string): AuthResource => {
  if (targetUrl) {
    try {
      const pathname = new URL(targetUrl, window.location.origin).pathname;
      const segments = pathname.split('/').filter(Boolean);
      if (segments.includes('me')) {
        return AuthResource.SELF_SERVICE;
      }
    } catch {
      // malformed URL, fall through to default
      console.error(`Failed to parse targetUrl: ${targetUrl}`);
    }
  }
  return AuthResource.AM_PORTAL;
};

/**
 * TODO Needs to be moved to common
 */
export function getTenantSubdomain(url: string): string {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    try {
      urlObj = new URL(url, window.location.origin);
    } catch {
      return '';
    }
  }
  // Split the hostname into parts
  const parts = urlObj.hostname.split('.');

  // Identify common domain patterns
  const domainParts = 2; // e.g., imprivata.com, imprivata.cloud, imprivata.eu
  const envPart = parts.some(part =>
    /^(dev|int|stg|eph|prod)(\d+)?$/.test(part),
  )
    ? 1
    : 0;
  const appPart = parts.some(part => part === 'app') ? 1 : 0;
  // Standard URL has: service + optional app + optional env + domain
  const standardParts = 1 + appPart + envPart + domainParts;

  // If we have more parts than the standard pattern, assume the first is a tenant ID - already exists
  // If we have more parts than the standard pattern, assume the first is a tenant ID
  if (parts.length > standardParts) {
    return parts[0];
  }
  return '';
}

function shouldUseTenantQueryParamInLocalDev(urlObj: URL): boolean {
  if (!import.meta.env.DEV) {
    return false;
  }

  const isDevAccessHost =
    urlObj.hostname === 'access.app.dev.imprivata.cloud' ||
    urlObj.hostname.endsWith('.access.app.dev.imprivata.cloud');

  // For local dev flow we keep host non-tenant and pass tenantId via query param.
  return isDevAccessHost && urlObj.port === '3000';
}

export function getUrlWithTenantId(url: string, tenantId: string): string {
  let urlObj: URL;
  try {
    urlObj = new URL(url);
  } catch {
    try {
      urlObj = new URL(url, window.location.origin);
    } catch {
      return url;
    }
  }

  if (shouldUseTenantQueryParamInLocalDev(urlObj)) {
    urlObj.hostname = 'access.app.dev.imprivata.cloud';
    urlObj.searchParams.set('tenantId', tenantId);
    return urlObj.toString();
  }

  urlObj.hostname = `${tenantId}.${urlObj.hostname}`;
  return urlObj.toString();
}
